/**
 * Opt-in debug logs for stage height / viewport math (resize, small screens).
 * In DevTools console: `localStorage.setItem('debugCanvasStage','1')` then reload.
 * Disable: `localStorage.removeItem('debugCanvasStage')` or set to '0'.
 */
function isCanvasStageDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("debugCanvasStage") === "1"
    );
  } catch {
    return false;
  }
}

let _lastStageDebugLogMs = 0;
function logStageHeightDebug(message, payload) {
  if (!isCanvasStageDebugEnabled()) return;
  if (typeof window !== "undefined" && window.__canvasStageDebugSilent) return;
  const now =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  if (now - _lastStageDebugLogMs < 350) return;
  _lastStageDebugLogMs = now;
}

/** Fixed Konva editor stage sizes (px); width is fixed; height caps here but shrinks on short viewports. */
export const FIXED_MAIN_CANVAS_STAGE_WIDTH_PX = 720;
export const FIXED_OFF_CANVAS_SIDE_WIDTH_PX = 720;

/** Horizontal “centered” scroll uses geometric center (symmetric left/right off-canvas reach at 100% zoom). */
// Positive value shifts viewport slightly left when centering main stage,
// so left off-canvas stays visible alongside main canvas on initial load.
export const CANVAS_HORIZONTAL_VIEW_BIAS_PX = 140;

/**
 * Left/right padding inside `.canvas-scroll-area` only (not Konva stage geometry).
 * Keep small — using the main stage width here only adds empty scroll margin around the fixed overlay.
 */
export const CANVAS_SCROLL_HORIZONTAL_GUTTER_PX = 24;
/** Design max height — used when the viewport is tall enough. */
export const FIXED_CANVAS_STAGE_HEIGHT_PX = 836;

/** Toolbar, zoom bar, margins: subtract from viewport so stages use the visible band. */
export const STAGE_VERTICAL_RESERVE_PX = 140;

/**
 * Minimum Konva stage height (px) when mapping canvas height % → pixels.
 * Keep small so values below ~30% actually shrink the white stage; the old 240px floor
 * made ~5–28% all look identical (~240px) while Canvas Height (%) still decreased.
 */
export const MIN_CANVAS_STAGE_HEIGHT_PX = 50;

/**
 * Best-effort visible viewport height (handles mobile URL bar / dynamic toolbars).
 */
export function getViewportHeightPx() {
  if (typeof window === "undefined") return FIXED_CANVAS_STAGE_HEIGHT_PX;
  const vv = window.visualViewport;
  const h = vv?.height ?? window.innerHeight;
  return Math.round(Math.max(1, h));
}

/**
 * Height used for canvas % ↔ px (stage, orb, resize drag). Uses the largest of
 * visual viewport, innerHeight, and documentElement.clientHeight so short screens
 * and mobile browsers don’t all map to an artificially small stage height.
 */
export function getEffectiveViewportHeightForStagePx() {
  if (typeof window === "undefined") return FIXED_CANVAS_STAGE_HEIGHT_PX;
  const vv = getViewportHeightPx();
  const inner = window.innerHeight;
  const client =
    typeof document !== "undefined"
      ? document.documentElement?.clientHeight || 0
      : 0;
  const result = Math.round(Math.max(1, vv, inner, client));
  logStageHeightDebug("getEffectiveViewportHeightForStagePx", {
    visualViewportHeight: vv,
    innerHeight: inner,
    documentClientHeight: client,
    eff: result,
  });
  return result;
}

/**
 * Fallback Konva stage height (px) — fixed design height, not viewport-based.
 */
export function getCanvasStageHeightPx() {
  return FIXED_CANVAS_STAGE_HEIGHT_PX;
}

/**
 * Stage height (px) from MyCanvas `canvasHeight` percent.
 * Uses fixed design height {@link FIXED_CANVAS_STAGE_HEIGHT_PX} (836) as 100% so
 * window resize does not change main/off-canvas stage pixel dimensions.
 * Matches resize orb when it uses the same fixed reference.
 */
export function getStageHeightPxFromCanvasHeight(canvasHeightPercent) {
  if (typeof window === "undefined") return FIXED_CANVAS_STAGE_HEIGHT_PX;
  const pct = parseInt(String(canvasHeightPercent ?? "100"), 10);
  if (Number.isNaN(pct)) return FIXED_CANVAS_STAGE_HEIGHT_PX;
  if (pct === 0) return 0;

  const designH = FIXED_CANVAS_STAGE_HEIGHT_PX;
  const rawFromPercent = (pct / 100) * designH;
  const finalPx = Math.min(
    designH,
    Math.max(MIN_CANVAS_STAGE_HEIGHT_PX, rawFromPercent),
  );
  logStageHeightDebug("getStageHeightPxFromCanvasHeight", {
    canvasHeightPercent: pct,
    designH,
    rawFromPercent,
    finalPx,
  });
  return finalPx;
}

/**
 * Call from DevTools: `window.__canvasStageDebug?.()` — prints viewport + stage height
 * (no localStorage flag required for this one-off snapshot).
 */
export function logCanvasStageDebugSnapshot(canvasHeightPercent = "100") {
  if (typeof window === "undefined") return;
  const vvApi = window.visualViewport;
  let stagePx;
  try {
    window.__canvasStageDebugSilent = true;
    stagePx = getStageHeightPxFromCanvasHeight(canvasHeightPercent);
  } finally {
    delete window.__canvasStageDebugSilent;
  }
  const clientH = document.documentElement?.clientHeight || 0;
  const scrollEl = document.querySelector(".canvas-scroll-area");
  const chromeEl = document.querySelector("[data-canvas-chrome]");
  const mainStageEl = document.querySelector("#main-canvas-stage-container");
  const scaledEl = document.querySelector("[data-app-zoom]");
  const snapshot = {
    canvasHeightPercent: String(canvasHeightPercent),
    _howToRead:
      "Stage height = (percent/100) × 836px (fixed design); it does not change when you resize the window. " +
      "App zoom (data-app-zoom) still scales how large it looks on screen.",
    visualViewport: vvApi
      ? {
          height: vvApi.height,
          width: vvApi.width,
          scale: vvApi.scale,
          offsetTop: vvApi.offsetTop,
          offsetLeft: vvApi.offsetLeft,
        }
      : null,
    innerHeight: window.innerHeight,
    outerHeight: window.outerHeight,
    screenAvailHeight: window.screen?.availHeight,
    documentClientHeight: clientH,
    documentScrollHeight: document.documentElement?.scrollHeight,
    getViewportHeightPx: getViewportHeightPx(),
    getEffectiveViewportHeightForStagePx: Math.round(
      Math.max(1, getViewportHeightPx(), window.innerHeight, clientH),
    ),
    getStageHeightPxFromCanvasHeight: stagePx,
    canvasScrollAreaClientHeight: scrollEl?.clientHeight ?? null,
    canvasScrollAreaClientWidth: scrollEl?.clientWidth ?? null,
    toolbarChromeHeight: chromeEl?.getBoundingClientRect?.().height ?? null,
    mainStageContainerRectHeight: mainStageEl?.getBoundingClientRect?.().height ?? null,
    appZoomFromDom: scaledEl?.getAttribute?.("data-app-zoom") ?? null,
    /** `.canvas-outer` transform/zoom (MyCanvas UI zoom), if present */
    canvasOuterStyle:
      typeof document !== "undefined"
        ? (() => {
            const el = document.querySelector(".canvas-outer");
            if (!el) return null;
            const cs = getComputedStyle(el);
            return { zoom: cs.zoom, transform: cs.transform };
          })()
        : null,
    zoomLevelCss: typeof document !== "undefined"
      ? Number.parseFloat(getComputedStyle(document.body).zoom || "1") || 1
      : 1,
  };
  return snapshot;
}

if (typeof window !== "undefined") {
  window.__canvasStageDebug = (pct) =>
    logCanvasStageDebugSnapshot(pct != null ? String(pct) : "100");
}

/** Total width of the off-canvas Stage: left strip + main overlay column. */
export function getOffCanvasStageWidthPx() {
  return FIXED_MAIN_CANVAS_STAGE_WIDTH_PX + FIXED_OFF_CANVAS_SIDE_WIDTH_PX;
}

/** @deprecated Use {@link getCanvasStageHeightPx} (responsive). */
export function getFixedCanvasStageHeightPx() {
  return getCanvasStageHeightPx();
}

/**
 * @deprecated Kept for legacy imports; stage width is fixed.
 */
export const MAIN_CANVAS_WIDTH_RATIO = 0.45;

/**
 * Main canvas stage width in pixels (fixed).
 * @param {number} [_viewportWidth] - Ignored; kept for call-site compatibility.
 */
export function getMainCanvasWidthPx(_viewportWidth) {
  return FIXED_MAIN_CANVAS_STAGE_WIDTH_PX;
}

/**
 * Max circle/ellipse radius so the shape can fit inside the main stage.
 */
export function getMainCanvasMaxRadiusPx(viewportWidth, stageHeightPx) {
  const halfW = getMainCanvasWidthPx(viewportWidth) / 2;
  const h = Number(stageHeightPx);
  const fallbackH = FIXED_CANVAS_STAGE_HEIGHT_PX / 2;
  const halfH = Number.isFinite(h) && h > 0 ? h / 2 : fallbackH;
  return Math.max(5, Math.min(halfW, halfH));
}
